'use client';

import * as DS from '@aurorasolar/ds';
import { x } from '@xstyled/styled-components';
import Image from 'next/image';
import Link from 'next/link';

import { useTracking } from '@/analytics';
import { GetStartedCta, H2, PagePreviewCard, Section, Subscribe } from '@/app/lp/components';
import { TRACKING } from '@/helpers/tracking';

import { NewsItem } from './newsItems';

type Props = { items: NewsItem[] };

const FeaturedNews = ({ item }: { item: NewsItem }) => {
  const { trackEvent } = useTracking();
  const url = `/home-solar/news/${item.slug}`;

  return (
    <DS.Flex bg="uiBgAlternate">
      <DS.Flex flexDirection={{ _: 'column', md: 'row' }} maxW={DS.BorealisTheme.screens.xl} mx="auto" rowGap={0}>
        <DS.Flex flex={1} minH={{ _: 300, md: 560 }} position="relative">
          <Image
            alt={item.image.alt}
            fill
            priority
            sizes="(max-width: 768px) 100vw, 50vw"
            src={item.image.src}
            style={{ objectFit: 'cover', objectPosition: 'center' }}
          />
        </DS.Flex>
        <DS.Flex flex={1} minH={300}>
          <DS.Flex gap={4} h="100%" justifyContent="center" p={{ _: 4, md: 6 }}>
            <DS.Text
              color="uiHelperCopy"
              fontWeight={700}
              letterSpacing="1.12px"
              text="body14"
              textTransform="uppercase"
            >
              {item.category} · {item.date}
            </DS.Text>
            <DS.Text as="h2" text={{ _: 'h1', md: 'd1' }} whiteSpace="wrap">
              {item.title}
            </DS.Text>
            <DS.Text text="body16" whiteSpace="wrap">
              {item.excerpt}
            </DS.Text>
            <DS.Button
              as={Link}
              href={url}
              icon={DS.IconArrowRight}
              iconPosition="right"
              mt={4}
              onClick={() => trackEvent(TRACKING.NEWS_READ_MORE, { page: item.slug })}
              size={{ _: 'sm', md: 'md' }}
              w="fit-content"
            >
              Read the release
            </DS.Button>
          </DS.Flex>
        </DS.Flex>
      </DS.Flex>
    </DS.Flex>
  );
};

export const NewsPage = ({ items }: Props) => {
  const [featured] = items;

  return (
    <article>
      <DS.Flex maxW={DS.BorealisTheme.screens.xl} mx="auto" px={{ _: 4, md: 6 }} py={{ _: 6, md: 8 }}>
        <DS.Text as="h1" text={{ _: 'd1', md: 'd2' }}>
          News
        </DS.Text>
        <DS.Text color="uiHelperCopy" text={{ _: 'body16', md: 'body20' }}>
          Announcements and press resources from the Aurora Solar Marketplace.
        </DS.Text>
      </DS.Flex>

      {featured ? <FeaturedNews item={featured} /> : null}

      {/* Heading and grid share one container so the heading-to-card gap stays tight (design review) */}
      <DS.Flex gap={5} maxW={DS.BorealisTheme.screens.xl} mx="auto" px={{ _: 6, lg: 10 }} py={{ _: 7, lg: 10 }}>
        <H2>All news</H2>
        <DS.Grid columnGap={4}>
          {items.map((item) => (
            <DS.GridItem
              alignItems="center"
              gap={4}
              justifyContent="center"
              key={item.slug}
              size={{ _: 'full', md: 'half' }}
            >
              <PagePreviewCard
                buttonProps={{ icon: DS.IconArrowRight, iconPosition: 'right', size: 'md' }}
                columnCount={2}
                description={item.excerpt}
                event={{ data: { page: item.slug }, name: TRACKING.NEWS_READ_MORE }}
                headline={item.title}
                image={{ alt: item.image.alt, src: item.image.src }}
                url={`/home-solar/news/${item.slug}`}
              />
            </DS.GridItem>
          ))}
        </DS.Grid>
      </DS.Flex>

      <Section align="center">
        <DS.Divider />
        <H2>Media inquiries</H2>
        <p>
          <span>Working on a story? Reach our communications team at </span>
          <x.a color="uiLink" href="mailto:press@aurorasolar.com">
            press@aurorasolar.com
          </x.a>
          <span>.</span>
        </p>
      </Section>

      <GetStartedCta eventName={TRACKING.NEWS_GET_QUOTE}>
        <Subscribe buttonText="Subscribe" variant="primary" />
      </GetStartedCta>
    </article>
  );
};
